import {
  DispositivoType,
  TrasferimentoModalita,
  useGetModelliDispositivo,
} from "@/api";
import { useGetDispositivi } from "@/api/dispositivi/dispositivi";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Typography from "@/elements/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import AziendaPicker from "../AziendaPicker";
// import CentralePicker from "../CentralePicker";
import DispositivoCard from "../DispositivoCard";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
// import RepartoPicker from "../RepartoPicker";
// import StabilimentoPicker from "../StabilimentoPicker";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import { Autocomplete, TablePagination, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import NodoDialog from "../NodoDialog";
import AziendaPicker from "../AziendaPicker";
import { useDebounce } from "@uidotdev/usehooks";
import { NodoPicker } from "../NodoPicker";
import { TipoNodoByTipoDispositivo } from "@/utils/dispositivoUtils";
import { portaleConfig } from "@/portaliConfig";

function DispositiviPage({ type }: { type: DispositivoType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const {
    filters,
    changeFilter,
    filtersLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePosizioneFilterWithNavigation(
    `/${type === "macchinario" ? "macchinari" : "sensori"}`,
    {
      enableNodo: true,
      enableModalita: true,
      enableTitolare: true,
      enablePagination: true,
      enableMatricola: true,
      enableModello: true,
    }
  );

  const [searchMatricola, setSearchMatricola] = useState<string | null>(null);
  const debouncedSearchMatricola = useDebounce(searchMatricola, 300) ?? null;
  const lastSearchMatricolaRef = useRef(filters.matricola ?? null);

  useEffect(() => {
    if (!filtersLoading) {
      setSearchMatricola(filters.matricola);
      lastSearchMatricolaRef.current = filters.matricola ?? null;
    }
  }, [filters.matricola, filtersLoading]);
  useEffect(() => {
    if (
      !filtersLoading &&
      lastSearchMatricolaRef.current !== debouncedSearchMatricola
    ) {
      changeFilter("matricola")(null, debouncedSearchMatricola);
      lastSearchMatricolaRef.current = debouncedSearchMatricola;
    }
  }, [debouncedSearchMatricola, filtersLoading]);

  const {
    data: dispositivi,
    isLoading,
    error,
  } = useGetDispositivi(
    portaleConfig.id,
    type,
    {
      page: page,
      pageSize: pageSize,
      modalita: filters.modalita ?? undefined,
      modelloDispositivoId: filters.modello?.id,
      padreId: filters.nodo?.id,
      searchMatricola: filters.matricola || undefined,
      titolareId: filters.titolare?.id ?? undefined,
    },
    {
      query: {
        enabled: !filtersLoading,
      },
    }
  );

  const [openDispositiviDialog, setOpenDispositiviDialog] = useState(false);

  const title = useMemo(() => {
    switch (type) {
      case "macchinario":
        return t("dispositiviPage.titoli.macchinari");
      case "sensore":
        return t("dispositiviPage.titoli.sensori");
      default:
        return t("dispositiviPage.titoli.misti");
    }
  }, [type, t]);

  const modelli = useGetModelliDispositivo();
  const modelliDisponibili = useMemo(() => {
    if (!modelli.data?.data) {
      return [];
    }

    return modelli.data.data.filter(
      (modello) => modello.tipoDispositivo === type
    );
  }, [modelli.data?.data, type]);

  if (filtersLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {title}
        </Typography>

        <Box display="flex" justifyContent="flex-end" sx={{ pt: 0.5 }}>
          {hasPermission("DispositivoCreate") && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setOpenDispositiviDialog(true);
              }}
            >
              {t("dispositiviPage.azioni.aggiungi")}
            </Button>
          )}
        </Box>
      </Box>

      <Box display="flex" gap={1} pt={1} flexWrap="wrap">
        {!user?.isEsterno && (
          <AziendaPicker
            type="aziendePortale"
            value={filters.titolare}
            onChange={changeFilter("titolare")}
            placeholder={t("dispositiviPage.filtri.proprietario")}
            size="small"
          />
        )}

        {!user?.isEsterno && (
          <Autocomplete<TrasferimentoModalita>
            id="modalita"
            onChange={changeFilter("modalita")}
            options={Object.values(TrasferimentoModalita)}
            getOptionLabel={(trasferimento) => {
              return `${trasferimento}`;
            }}
            isOptionEqualToValue={(option, value) => option === value}
            value={filters.modalita}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("dispositiviPage.filtri.modalita")}
              />
            )}
            size="small"
          />
        )}

        <TextField
          placeholder={t("dispositiviPage.filtri.matricola")}
          value={searchMatricola}
          onChange={(e) => {
            setSearchMatricola(e.target.value || "");
          }}
          size="small"
        />

        <Autocomplete
          onChange={changeFilter("modello")}
          value={filters.modello}
          options={modelliDisponibili}
          getOptionLabel={(m) => m.modello}
          renderInput={(props) => (
            <TextField
              {...props}
              placeholder={t("dispositiviPage.filtri.modello")}
            />
          )}
          size="small"
        />
      </Box>

      <Box mt={1}>
        <NodoPicker
          grow="horizontally"
          value={filters.nodo}
          onChange={(x) => changeFilter("nodo")(null, x)}
        />
      </Box>

      {isLoading && <Loading />}

      <Box
        mt={2}
        mb={0}
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
      >
        {dispositivi?.data?.dispositivi?.map((dispositivo) => {
          return (
            <DispositivoCard
              key={dispositivo.id}
              dispositivo={dispositivo}
              proprietaMisurate={dispositivo.dispositivoProprietaMisurate || []}
              showPosizione
            />
          );
        })}
        {dispositivi?.data?.dispositivi &&
          dispositivi?.data?.dispositivi.length === 0 && (
            <Typography variant="body2">
              {t("dispositiviPage.stati.nessunDispositivo")}
            </Typography>
          )}
      </Box>

      {dispositivi?.data && (
        <TablePagination
          component="div"
          page={page}
          count={dispositivi.data.count}
          onPageChange={(event, page) => {
            setPage(page);
          }}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value));
          }}
        />
      )}

      <NodoDialog
        open={openDispositiviDialog}
        tipoNodo={TipoNodoByTipoDispositivo[type]}
        onClose={(d) => {
          setOpenDispositiviDialog(false);
          if (d) {
            navigate(`/nodi/${d.id}`);
          }
        }}
      />
    </Box>
  );
}

export default DispositiviPage;
